// src/services/taskServices/TaskRelationshipService.ts
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { CreateRelationshipDto, TaskRelationshipType, TemplateRelationship, TemplateRelationshipResponse } from "../../interfaces/taskRelationship/taskRelationship";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

// --- TemplateTask (Recurring) Relationships ---

/**
 * Get all relationships for a recurring TemplateTask
 */
export async function getTemplateTaskRelationships(
  authToken: string,
  templateId: number
): Promise<TemplateRelationshipResponse[] | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/templatetask/${templateId}/relationships`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      console.log(await response.json());
      return null;
    }

    return (await response.json()) as TemplateRelationshipResponse[];
  } catch (error) {
    console.error(`Error fetching relationships for template task ${templateId}:`, error);
    return null;
  }
}

/**
 * Create a new relationship for a recurring TemplateTask
 */
export async function createTemplateTaskRelationship(
  authToken: string,
  sourceTemplateId: number,
  dto: CreateRelationshipDto
): Promise<ApiResponse<TemplateRelationship | null>> {
  try {
    const response = await fetch(`${API_BASE_URL}/templatetask/${sourceTemplateId}/relationships`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dto),
    });

    return await response.json();
  } catch (error) {
    throw error;
  }
}

/**
 * Delete a relationship for a recurring TemplateTask
 */
export async function deleteTemplateTaskRelationship(
  authToken: string,
  sourceId: number,
  targetId: number,
  typeId: number
): Promise<ApiResponse<boolean>> {
  try {
    const response = await fetch(`${API_BASE_URL}/templatetask/${sourceId}/relationships/${targetId}/${typeId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    return await response.json();
  } catch (error) {
    throw error;
  }
}


// --- TemplateInterimTask (On-Demand) Relationships ---

/**
 * Get all relationships for an on-demand TemplateInterimTask
 */
export async function getInterimTaskRelationships(
  authToken: string,
  templateId: number
): Promise<TemplateRelationshipResponse[] | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/templatetask/interim/${templateId}/relationships`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      console.log(await response.json());
      return null;
    }

    return (await response.json()) as TemplateRelationshipResponse[];
  } catch (error) {
    console.error(`Error fetching relationships for interim task ${templateId}:`, error);
    return null;
  }
}

/**
 * Create a new relationship for an on-demand TemplateInterimTask
 */
export async function createInterimTaskRelationship(
  authToken: string,
  sourceTemplateId: number,
  dto: CreateRelationshipDto
): Promise<ApiResponse<TemplateRelationship | null>> {
  try {
    const response = await fetch(`${API_BASE_URL}/templatetask/interim/${sourceTemplateId}/relationships`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dto),
    });

    return await response.json();
  } catch (error) {
    throw error;
  }
}

/**
 * Delete a relationship for an on-demand TemplateInterimTask
 */
export async function deleteInterimTaskRelationship(
  authToken: string,
  sourceId: number,
  targetId: number,
  typeId: number
): Promise<ApiResponse<boolean>> {
  try {
    // Note: The endpoint path uses "templateinterimtask"
    const response = await fetch(`${API_BASE_URL}/templatetask/interim/${sourceId}/relationships/${targetId}/${typeId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    return await response.json();
  } catch (error) {
    throw error;
  }
}

export async function getTaskRelationshipTypes(
  authToken: string
): Promise<TaskRelationshipType[] | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/templatetask/taskrelationshiptypes`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      console.log(await response.json());
      return null;
    }

    return (await response.json()) as TaskRelationshipType[];
  } catch (error) {
    console.error("Error fetching task relationship types:", error);
    return null;
  }
}